import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import { Container, Typography, Button, Stack, Alert } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { get } from "src/utils/networkUtils";
import AdminsTable from "src/components/admins/AdminsTable";
import AdminRowMenu from "src/components/admins/AdminRowMenu";
import InviteAdminDialog from "src/components/admins/InviteAdminDialog";
import EditAdminDialog from "src/components/admins/EditAdminDialog";
import ResetPasswordDialog from "src/components/admins/ResetPasswordDialog";
import DeactivateAdminDialog from "src/components/admins/DeactivateAdminDialog";

function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem("user")) ?? null;
  } catch {
    return null;
  }
}

export default function AdminsPage() {
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // row menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);

  // dialogs
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const loadAdmins = useCallback(() => {
    setLoading(true);
    setError(null);
    get("/admin/users")
      .then((res) => setAdmins(res?.data ?? []))
      .catch(() => setError("Failed to load admin users."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const openMenu = (event, admin) => {
    setMenuAnchor(event.currentTarget);
    setMenuTarget(admin);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const handleEdit = (admin) => {
    closeMenu();
    setEditTarget(admin);
  };
  const handleReset = (admin) => {
    closeMenu();
    setResetTarget(admin);
  };
  const handleDeactivate = (admin) => {
    closeMenu();
    setDeactivateTarget(admin);
  };

  return (
    <>
      <Helmet>
        <title>Admins | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Typography variant="h4">Admin Users</Typography>
          {isSuperAdmin && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Invite Admin
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AdminsTable
          admins={admins}
          loading={loading}
          isSuperAdmin={isSuperAdmin}
          currentUserId={currentUser?.adminUserId}
          onOpenMenu={openMenu}
        />
      </Container>

      <AdminRowMenu
        anchorEl={menuAnchor}
        target={menuTarget}
        onClose={closeMenu}
        onEdit={handleEdit}
        onReset={handleReset}
        onDeactivate={handleDeactivate}
      />

      <InviteAdminDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={loadAdmins}
      />

      <EditAdminDialog
        open={Boolean(editTarget)}
        admin={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={loadAdmins}
      />

      <ResetPasswordDialog
        open={Boolean(resetTarget)}
        admin={resetTarget}
        onClose={() => setResetTarget(null)}
      />

      <DeactivateAdminDialog
        open={Boolean(deactivateTarget)}
        admin={deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onSuccess={loadAdmins}
      />
    </>
  );
}
